import { DataModule } from "./DataModule.js";
import * as dtt from "../../data/model/data-types.js";
import * as dto from "../../data/model/data-objects.js";
import * as browserHelpers from "../utils/data-browser-helpers.js";
import * as editorHelpers from "../utils/data-editor-helpers.js";
import { DataQueryManager } from "../../data/queries/DataQueryManager.js";
import { DataTable } from "../controls/data/DataTable.js";
import { DataEditorControl } from "../controls/data/DataEditorControl.js";
import { ModalDialog } from "../controls/dialogs/ModalDialog.js";
import { Button } from "../controls/Button.js";
import { Text } from "../controls/Text.js";
import { resolveValue } from "../utils/ui-helpers.js";

export class DataBrowser extends DataModule {

    static MODE_READ = 'read';
    static MODE_UPDATE = 'update';

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: () => this.getDataModel().getEntityName() + ':Browser',
            moduleTitle: () => this.getDataModel().getSetLabel(),
        }, options);

        super(parent, options);

        const {
            defaultDataQueryString = () => {
                const config = this.getConfig();
                return `$limit=${config.paginator.defaultPageSize}`;
            },
            browserRouteName = null,
            editorRouteName = null,
        } = options;

        this.columnDefs = options.columnDefs ?? null;
        this.browserFields = options.browserFields ?? null;
        this.editorDefs = options.editorDefs ?? null;
        this.editorFields = options.editorFields ?? null;
        this.resolvedColumnDefs = null;
        this.resolvedEditorDefs = null;

        this.dataFilterMode = DataBrowser.MODE_READ;
        this.viewMode = DataBrowser.MODE_READ;

        this.dataset = {
            count: 0,
            limit: null,
            offset: 0,
            items: [],
        };
        this.columns = [];

        this.defaultDataQueryString = defaultDataQueryString;
        this.browserRouteName = browserRouteName;
        this.editorRouteName = editorRouteName;
        this.queryManager = DataQueryManager.create(this, {
            defaultQueryString: () => this.getDefaultDataQueryString(),
            fields: () => {
                const model = this.getDataModel();
                return (model && model.getFields()) || [];
            },
            onQueryChange: () => {
                this.applyFilter();
            },
        });
        this.queryManager.setQueryString(this.getDefaultDataQueryString());

        this.activeItem = null;
        this.activeItemBackup = null;
        this.activeItemEditor = null;
        this.editDialog = null;

        this.uiDataTable = new DataTable(this, {
            data: () => this.getData(),
            dataModel: () => this.getDataModel(),
            queryManager: () => this.getQueryManager(),
            columns: () => this.getColumns(),
            userCanReadData: () => this.canUserReadData(),
            userCanUpdateData: () => this.canUserUpdateData(),
            userCanDeleteData: () => this.canUserDeleteData(),
            onSetAllItemsSelection: (value) => this.setAllItemsSelection(value),
            onSetAllItemsDetails: (value) => this.setAllItemsDetails(value),
            onToggleItemSelection: (item) => this.toggleItemSelection(item),
            onToggleItemDetails: (item) => this.toggleItemDetails(item),
            onOpenItem: (item) => this.openItem(item),
            onEditItem: (item) => this.editItem(item),
            onDeleteItem: (item) => this.deleteItem(item),
        });

    };

    // --------------------------------------------------------------------------

    getDataFilterMode() {

        return this.dataFilterMode;

    };

    getViewMode() {

        return this.viewMode;

    };

    setViewMode(mode) {

        this.viewMode = mode || DataBrowser.MODE_READ;
        return this.viewMode;

    };

    changeViewMode(mode, invalidate = true) {

        this.setViewMode(mode);
        if (invalidate)
            this.invalidate();
        return this.viewMode;

    };

    toggleDataFilterMode() {

        this.dataFilterMode = (this.dataFilterMode == DataBrowser.MODE_READ ? DataBrowser.MODE_UPDATE : DataBrowser.MODE_READ);
        this.invalidate();

    };

    // --------------------------------------------------------------------------

    getDataset() {

        return (typeof this.dataset == 'function') ? this.dataset.call(this) : this.dataset;

    };

    setDataset(dataset) {

        this.dataset = dataset || {
            count: 0,
            limit: null,
            offset: 0,
            items: [],
        };

        return this.dataset;

    };

    setData(data) {

        const dataset = this.getDataset() || {};
        dataset.items = data || [];
        this.setDataset(dataset);

    };

    getData() {

        let ds = this.getDataset();
        return (ds && ds.items) || [];

    };

    getSelectedItems() {

        return (this.getData() || []).filter(function (item) { return item.$selected; });

    };

    getActiveItem() {

        return this.activeItem;

    };

    setActiveItem(item, suppressRefresh) {

        this.activeItem = item;
        if (!suppressRefresh) {
            this.refresh();
        }

    };

    // --------------------------------------------------------------------------

    getQueryManager() { return this.queryManager; }

    getDefaultDataQueryString() { return resolveValue(this.defaultDataQueryString); }

    getDataQueryString() {

        var qm = this.getQueryManager();
        return qm.getDataQueryString();
        //return (query && query.compile()) || null;

    };

    prepareDataQuery() {

        const defaults = this.getDefaultDataQueryString();
        this.getQueryManager().setQueryString(defaults);

    };

    getModuleState() {

        return {
            queryString: this.getDataQueryString(),
            viewMode: this.getViewMode(),
            dataFilterMode: this.getDataFilterMode(),
        };

    };

    setModuleState(state) {

        if (!state)
            return;

        if (state.queryString) {
            this.getQueryManager().setQueryString(state.queryString);
        }

        if (state.viewMode) {
            this.setViewMode(state.viewMode);
        }

        if (state.dataFilterMode) {
            this.dataFilterMode = state.dataFilterMode;
        }

    };

    // --------------------------------------------------------------------------

    getEditorRouteName() {

        return this.editorRouteName
            || this.getDataModel()?.editor?.moduleName
            || null;

    };

    getBrowserRouteName() {

        return this.browserRouteName
            || this.getDataModel()?.browser?.moduleName
            || null;

    };

    getItemRoute(id) {

        return this.getApp().getRouteHash(this.getEditorRouteName(), { id: id });

    };

    getItemUrl(id) {

        return this.getApp().getRouteUrl(this.getEditorRouteName(), { id: id });

    };

    getApiUrl() {

        var service = this.getDataModel();
        return (service ? service.getDataServicePath() : '');

    };

    getExportUrl(format, ids) {

        let queryManager = this.getQueryManager(),
            queryString = queryManager.getDataQueryString({ compileLimit: false, compileOffset: false }) || '';
        return this.getApiUrl() + '?' + (queryString || '') + '&format=' + (format || 'csv') + (ids ? '&ids=' + ids : '');

    };

    // --------------------------------------------------------------------------

    getBrowsableFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.inBrowser);

    };

    getFilterableFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.filterable);

    };

    getPreviewFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.editable && !dtt.isCodelistType(field.datatype));

    };

    getNewRecordFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.required && !field.readOnly && (field.editable || field.inEditor));

    };

    getActiveItemEditor() {

        return this.activeItemEditor || null;

    };

    createActiveItemEditor(item) {

        return new DataEditorControl(null, {
            data: () => item,
            dataModel: () => this.getDataModel(),
            editorDefs: () => this.getEditorDefs(),
            readOnly: false,
            canUserEditField: () => true,
            onFieldValueChange: (value, field, actualData) => {
                field.setValue(actualData, value);
                actualData.$changed = true;
                this.invalidate();
                return true;
            }
        });

    };

    // --------------------------------------------------------------------------

    getColumns() {

        return this.columns;

    };

    getColumnsDefs() {

        return this.ensureColumnDefs();

    };

    getConfiguredColumnDefs() {

        return typeof this.columnDefs == 'function'
            ? this.columnDefs.call(this)
            : this.columnDefs;

    };

    getConfiguredBrowserFields() {

        return typeof this.browserFields == 'function'
            ? this.browserFields.call(this)
            : this.browserFields;

    };

    resolveColumnDefs() {

        const configuredColumnDefs = this.getConfiguredColumnDefs();
        if (configuredColumnDefs !== null && configuredColumnDefs !== undefined)
            return configuredColumnDefs;

        const configuredBrowserFields = this.getConfiguredBrowserFields();
        if (configuredBrowserFields !== null && configuredBrowserFields !== undefined)
            return browserHelpers.createColumnDefsFromFieldNames(this.getDataModel(), configuredBrowserFields);

        return browserHelpers.createColumnDefsFromModel(this.getDataModel());

    };

    ensureColumnDefs(force = false) {

        if (!force && this.resolvedColumnDefs !== null)
            return this.resolvedColumnDefs;

        this.resolvedColumnDefs = this.resolveColumnDefs() || [];
        return this.resolvedColumnDefs;

    };

    getConfiguredEditorDefs() {

        return typeof this.editorDefs == 'function'
            ? this.editorDefs.call(this)
            : this.editorDefs;

    };

    getConfiguredEditorFields() {

        return typeof this.editorFields == 'function'
            ? this.editorFields.call(this)
            : this.editorFields;

    };

    resolveEditorDefs() {

        const configuredEditorDefs = this.getConfiguredEditorDefs();
        if (configuredEditorDefs !== null && configuredEditorDefs !== undefined)
            return configuredEditorDefs;

        const configuredEditorFields = this.getConfiguredEditorFields();
        if (configuredEditorFields !== null && configuredEditorFields !== undefined)
            return editorHelpers.createEditorDefsFromFieldNames(this.getDataModel(), configuredEditorFields);

        return editorHelpers.createEditorDefsFromModel(this.getDataModel());

    };

    ensureEditorDefs(force = false) {

        if (!force && this.resolvedEditorDefs !== null)
            return this.resolvedEditorDefs;

        this.resolvedEditorDefs = this.resolveEditorDefs() || [];
        return this.resolvedEditorDefs;

    };

    getEditorDefs() {

        return this.ensureEditorDefs();

    };

    prepareColumns() {

        var columnsDefs = this.getColumnsDefs() || [],
            queryManager = this.getQueryManager(),
            orderBy = (queryManager && queryManager.getQueryOrder()) || [];

        if (columnsDefs.length) {
            this.columns = browserHelpers.createColumns(columnsDefs, orderBy);
            return this.columns;
        }

        this.columns = browserHelpers.createColumnsFromModel(this.getDataModel(), orderBy);
        return this.columns;

    };

    // --------------------------------------------------------------------------

    isReloadButtonEnabled() {
        return this.canUserReadData();
    };

    isEditButtonEnabled() {
        return this.getViewMode() == DataBrowser.MODE_READ && this.canUserReadData();
    };

    isApplyButtonEnabled() {
        return false;
    };

    isCancelButtonEnabled() {
        return false;
    };

    isDeleteButtonEnabled() {
        var selectedItems = this.getSelectedItems();
        return selectedItems && selectedItems.length && this.getViewMode() == DataBrowser.MODE_READ && this.canUserDeleteData();
    };

    isCreateButtonEnabled() {
        return this.getViewMode() == DataBrowser.MODE_READ && this.canUserCreateData();
    };

    isCloseButtonEnabled() {
        return true;
    };

    // --------------------------------------------------------------------------

    applyFilter() {

        this.storeModuleState();
        this.load();

    };

    applyQueryString(queryString) {

        let dataQuery = this.getQueryManager();
        dataQuery.setQueryString(queryString);
        this.applyFilter();

    };

    edit() {

        this.editItem(this.getActiveItem());

    };

    create() {

        var modalDialog = this.getNewRecordEditorControls();

        modalDialog.show(this.getApp());

    };

    delete() {

        this.deleteItem(this.getActiveItem());

    };

    close() {
        this.getApp().navigateBack();
    };

    // --------------------------------------------------------------------------

    getNewRecordEditorControls() {

        const i18n = this.getI18n();

        var that = this,
            model = this.getDataModel(),
            fields = this.getNewRecordFields(),
            newRecord = (fields || []).reduce(function (rec, field) { rec[field.name] = null; return rec; }, {}),
            modal = new ModalDialog(null, {
                title: model.getSetLabel() + ' – ' + i18n.t('ui.title.new_record'),
                controls: [],
                buttons: [
                    new Button(null, {
                        label: i18n.t('button.save.label'),
                        icon: '#confirm',
                        disabled: () => !(fields || []).every((field) => field.validateValue(newRecord)),
                        onClick: onNewRecordConfirmed,
                    }),
                    new Button(null, {
                        label: i18n.t('button.cancel.label'),
                        icon: '#cancel',
                        css: '.outlined',
                        onClick: onNewRecordCanceled,
                    }),
                ]
            });

        modal.controls = editorHelpers.createEditorGroupsFromFields(
            modal,
            fields,
            { data: () => newRecord },
            { grid: true }
        );

        return modal;

        async function onNewRecordConfirmed() {
            that.getApp().setBusyFlag();
            try {
                const data = await that.getDataManager().createData(newRecord);
                modal.hide();
                that.getApp().routeTo(that.getEditorRouteName(), { id: data.id });
                return true;
            } catch (error) {
                that.handleError(error);
                return false;
            } finally {
                that.getApp().clearBusyFlag();
            }
        };

        function onNewRecordCanceled() {
            modal.hide();
        };

    };

    // --------------------------------------------------------------------------

    toggleItemSelection(item) {

        const i18n = this.getI18n();

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        item.$selected = !item.$selected;
        this.invalidate();

    };

    toggleItemDetails(item) {

        const i18n = this.getI18n();

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        item.$details = !item.$details;
        this.invalidate();

    };

    setAllItemsSelection(selected) {

        (this.getData() || []).forEach(function (item) { item.$selected = selected; });
        this.invalidate();

    };

    setAllItemsDetails(details) {

        (this.getData() || []).forEach(function (item) { item.$details = details; });
        this.invalidate();

    };

    // --------------------------------------------------------------------------

    removeItem(item) {

        var data = this.getData(),
            index = data.indexOf(item);

        if (index < 0)
            return;

        data.splice(index, 1);
        this.invalidate();

    };

    openItem(item) {

        const i18n = this.getI18n();

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        this.getApp().routeTo(this.getEditorRouteName(), { id: item.id });

    };

    deleteItem(item) {

        const i18n = this.getI18n();

        var that = this,
            model = this.getDataModel(),
            modal = new ModalDialog(null, {
                title: i18n.t('module.browser.record_delete_title'),
                controls: [
                    i18n.t('module.browser.record_delete_confirmation', { name: model.getTitle(item) }),
                ],
                buttons: [
                    new Button(null, {
                        label: i18n.t('button.yes.label'),
                        icon: '#confirm',
                        css: '.outlined',
                        onClick: deleteConfirmed
                    }),
                    new Button(null, {
                        label: i18n.t('button.no.label'),
                        icon: 'cancel',
                        onClick: deleteCanceled
                    })
                ]
            });

        modal.show(this.getApp());

        async function deleteConfirmed() {

            modal.hide();

            that.getApp().setBusyFlag();
            try {
                await that.getDataManager().deleteData(item);
                that.getLogger().success(i18n.t('app.record_deleted'));
                await that.load();
            } catch (error) {
                that.getLogger().error(i18n.t('app.record_delete_failed_details', { details: error }));
            } finally {
                that.getApp().clearBusyFlag();
            }

        };

        function deleteCanceled(e) {
            modal.hide();
        };

    };

    editItem(item) {

        this.checkActiveItemChangesAndExecute(() => {

            this.activeItem = item;
            this.activeItemBackup = Object.assign({}, item);
            this.openEditDialog(item);

        });

    };

    openEditDialog(item) {

        const i18n = this.getI18n();
        const model = this.getDataModel();
        const editor = this.createActiveItemEditor(item);
        const modal = new ModalDialog(null, {
            title: model.getTitle(item),
            css: '.medium',
            controls: [editor],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.save.label'),
                    icon: '#confirm',
                    disabled: () => !item?.$changed || !editor.validateData(),
                    onClick: () => this.commitActiveItemChanges(),
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => this.cancelActiveItemChanges(),
                }),
            ]
        });

        this.setViewMode(DataBrowser.MODE_UPDATE, false);
        this.activeItemEditor = editor;
        this.editDialog = modal;
        modal.show(this);
        this.invalidate();

    };

    checkActiveItemChangesAndExecute(execute) {

        const i18n = this.getI18n();

        var that = this,
            activeItem = this.getActiveItem(),
            dataModel = this.getDataModel(),
            modal;

        if (!activeItem || !activeItem.$changed) {
            execute.call(this);
            return;
        }

        modal = new ModalDialog(null, {
            title: i18n.t('module.browser.drop_unsaved_changes_title'),
            controls: [
                i18n.t('module.module.browser.drop_unsaved_changes_confirmation', { name: dataModel.getTitle(activeItem) }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.yes.label'),
                    icon: '#confirm',
                    css: '.outlined',
                    onClick: onDropUnsavedChangesConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.no.label'),
                    icon: '#cancel',
                    onClick: onDropUnsavedChangesRejected
                })
            ]
        });

        modal.show(this);

        function onDropUnsavedChangesConfirmed(e) {

            e.preventDefault();
            modal.hide();
            that.cancelActiveItemChanges();
            execute.call(that);

        };

        function onDropUnsavedChangesRejected(e) {

            e.preventDefault();
            modal.hide();

        };

    };

    changeActiveItemProp(name, value) {

        dto.changeObjectProp(this.activeItem, name, value, this.activeItemValidator);
        this.invalidate();

    };

    async commitActiveItemChanges() {

        const i18n = this.getI18n();

        var that = this,
            manager = this.getDataManager(),
            data = dto.getObjectData(this.activeItem);

        this.getApp().setBusyFlag();
        try {
            const updatedData = await manager.updateData(data);
            const updatedId = updatedData?.id ?? data?.id ?? this.activeItem?.id ?? null;
            const refreshedData = updatedId ? await manager.readData(updatedId) : null;
            that.getLogger().success(i18n.t('app.record_updated'));
            if (refreshedData)
                Object.assign(that.activeItem, refreshedData);
            that.activeItem.$changed = false;
            that.activeItem.$details = true;
            that.setViewMode(DataBrowser.MODE_READ);
            if (that.editDialog)
                that.editDialog.hide();
            that.activeItem = null;
            that.activeItemBackup = null;
            that.activeItemEditor = null;
            that.editDialog = null;
            that.invalidate();
        } catch (error) {
            that.getLogger().error(i18n.t('app.record_update_failed_details', { details: error }));
            that.invalidate();
        } finally {
            that.getApp().clearBusyFlag();
        }

    };

    cancelActiveItemChanges() {

        Object.assign(this.activeItem, this.activeItemBackup);
        this.activeItem.$changed = false;
        this.activeItem.$details = true;
        this.setViewMode(DataBrowser.MODE_READ);
        if (this.editDialog)
            this.editDialog.hide();
        this.activeItem = null;
        this.activeItemBackup = null;
        this.activeItemEditor = null;
        this.editDialog = null;
        this.invalidate();

    };

    deleteSelectedItems() {

        const i18n = this.getI18n();

        var that = this,
            dataModel = this.getDataModel(),
            selectedItems = this.getSelectedItems(),
            promises,
            modal;

        if (!selectedItems || !selectedItems.length)
            return;

        modal = new ModalDialog(null, {
            title: i18n.t('module.browser.records_delete_title'),
            controls: [
                i18n.t('module.browser.records_delete_confirmation',
                    {
                        names: selectedItems.reduce(function (result, item) {
                            return (result ? result + ', ' : '') + dataModel.getTitle(item);
                        }, null)
                    }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.yes.label'),
                    icon: '#confirm',
                    css: '.outlined',
                    onClick: deleteItemConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.no.label'),
                    icon: '#cancel',
                    onClick: deleteItemCanceled
                })
            ]
        });

        modal.show(this.getApp());

        async function deleteItemConfirmed() {

            modal.hide();

            that.getApp().setBusyFlag();
            try {
                promises = selectedItems.map(async function (item) {
                    try {
                        await that.getDataManager().deleteData(item);
                        that.getLogger().success(i18n.t('app.record_deleted'));
                        that.removeItem(item);
                    } catch (error) {
                        that.getLogger().error(i18n.t('app.record_delete_failed_details', { details: error }));
                    }
                });

                await Promise.all(promises);
                await that.load();
            } catch (error) {
                that.invalidate();
            } finally {
                that.getApp().clearBusyFlag();
            }

        };

        function deleteItemCanceled(e) {
            modal.hide();
        };

    };

    // --------------------------------------------------------------------------

    async _prepare(params) {

        await super._prepare(params);
        this.ensureColumnDefs(true);
        this.ensureEditorDefs(true);

        await Promise.all([
            this.prepareDataQuery(),
            this.prepareColumns()
        ]);

        this.restoreModuleState();

        return this.readData();

    };

    async load(options, activeRoute) {

        await this.readData(options, activeRoute);
        this.invalidate();
        return this.getDataset();

    };

    async readData() {

        var queryString = this.getDataQueryString();

        try {

            this.setDataset(await this.getDataManager().queryData(queryString));

        } catch (error) {

            this.setDataset({});
            throw error;

        }

    };

    // --------------------------------------------------------------------------

    changeDataQueryOrder(columnDef, additive) {

        var query = this.getQueryManager();
        query.updateOrder(columnDef.name, additive);
        this.load();

    };

    changeDataQueryOffset(value) {

        this.getQueryManager().changeQueryOffset(value);
        this.load();

    };

    changeDataQueryLimit(value) {

        this.getQueryManager().changeQueryLimit(value);
        this.load();

    };

    changeDataPagination(value) {

        const dataset = this.getDataset() || {};
        const queryManager = this.getQueryManager();

        if (value.limit !== undefined && value.limit !== null && value.limit != dataset.limit)
            queryManager.changeQueryLimit(value.limit);
        if (value.offset !== undefined && value.offset !== null && value.offset != dataset.offset)
            queryManager.changeQueryOffset(value.offset);

        this.load();

    };

    // --------------------------------------------------------------------------------------------

    getExportFormats() {

        return [];

    };

    getExportSelectedFormats() {

        return this.getExportFormats();

    };

    isNewRecordButtonAvailable() {

        return this.getNewRecordFields().length > 0;

    };

    renderPrimaryButtons(r) {

        return [
            r.iconButton('#refresh', {
                css: '.circle.transparent',
                props: {
                    onclick: () => { this.load(); },
                    disabled: !this.isReloadButtonEnabled(),
                },
            })
        ];

    };

    renderSecondaryButtons(r) {

        const i18n = this.getI18n();

        if (!this.isNewRecordButtonAvailable())
            return [];

        return [
            r.button(i18n.t('button.add.label'), '#add', {
                props: {
                    disabled: !this.isCreateButtonEnabled(),
                    onclick: () => { this.create(); },
                },
            })
        ];

    }

    renderExportButton(r, formatIds, ids = null, bottom = false) {

        const i18n = this.getI18n();
        const formats = formatIds || [];
        const selectedMode = !!ids;

        if (!formats.length)
            return null;

        const items = [
            formats.includes('csv')
                ? renderExportItem('csv', '#file-csv', 'ui.label.export_csv', selectedMode ? 'ui.label.export_selected_csv_hint' : 'ui.label.export_csv_hint')
                : null,
            formats.includes('excel-xml')
                ? renderExportItem('excel-xml', '#file-excel', 'ui.label.export_xml_excel', selectedMode ? 'ui.label.export_selected_xml_excel_hint' : 'ui.label.export_xml_excel_hint')
                : null,
            formats.includes('pdf')
                ? renderExportItem('pdf', '#file-pdf', 'ui.label.export_pdf_report', selectedMode ? 'ui.label.export_selected_pdf_report_hint' : 'ui.label.export_pdf_report_hint')
                : null,
            formats.includes('pdf-zip')
                ? renderExportItem('pdf-zip', '#file-archive', 'ui.label.export_pdf_report_zip', selectedMode ? 'ui.label.export_selected_pdf_report_zip_hint' : 'ui.label.export_pdf_report_zip_hint')
                : null,
        ].filter(Boolean);

        return r.dropdownButton(
            i18n.t(ids ? 'button.export_selected' : 'button.export_all'),
            'download',
            {
                anchor: bottom ? 'top right' : 'bottom right',
                props: {
                    title: i18n.t(selectedMode ? 'button.export_selected_hint' : 'button.export_all_hint'),
                },
            },
            items
        );

        const renderExportItem = (format, icon, labelKey, titleKey) => {
            return r.menuItem(null, null, {}, null, [
                r.a({ props: { href: this.getExportUrl(format, ids), target: '_blank', title: i18n.t(titleKey) } }, [
                    r.icon(icon),
                    ' ',
                    i18n.t(labelKey)
                ])
            ]);
        };

    };

    renderHeaderContent(r, vm) {

        return r.nav({}, [
            ...this.renderPrimaryButtons(r),
            r.div('.max'),
            ...this.renderSecondaryButtons(r)
        ]);

    };

    renderFooterContent(r, vm) {

        const dataset = this.getDataset() || {};

        return [
            this.renderPaginator(r, dataset),
        ];

    }

    renderPaginator(r, dataset, options = null) {

        const config = this.getConfig();
        const cfg = config.paginator || {};
        const count = dataset?.count ?? 0;
        const limit = dataset?.limit || cfg.defaultPageSize || 25;
        const offset = dataset?.offset ?? 0;

        return r.paginator(count, limit, offset, {
            pageLimits: cfg.availablePageSizes,
            onValueSet: (state) => this.changeDataPagination(state),
            ...(options ?? {}),
        });

    }

    renderToolbar(r, vm, bottom = false) {

        const i18n = this.getI18n();

        const dataset = this.getDataset() || {};
        const selectedItems = this.getSelectedItems();
        const selectedIds = selectedItems.reduce((result, item) => {
            if (!(item && item.id))
                return result;
            return result ? `${result}+${item.id}` : item.id.toString();
        }, null);

        const cells = [
            r.cell({ css: '.shrink.mr-2' }, this.renderPrimaryButtons(r)),
            r.cell({ css: '.shrink.mr-2' }, [
                this.renderPaginator(r, dataset)
            ]),
            r.cell({ css: '.grow' }),
        ];

        if (this.isCreateButtonEnabled() && this.isNewRecordButtonAvailable()) {
            cells.push(
                r.cell({ css: '.shrink.ml-1' }, this.renderSecondaryButtons(r))
            );
        }

        if (this.canUserReadData()) {
            const exportAllButton = this.renderExportButton(r, this.getExportFormats(), null, bottom);
            if (exportAllButton) {
                cells.push(
                    r.cell({ css: '.shrink.ml-1' }, [
                        exportAllButton
                    ])
                );
            }
        }

        if (selectedItems.length) {
            const selectedControls = [];
            const exportSelectedButton = this.canUserReadData()
                ? this.renderExportButton(r, this.getExportSelectedFormats(), selectedIds, bottom)
                : null;

            if (exportSelectedButton) {
                selectedControls.push(exportSelectedButton);
            }

            if (this.canUserDeleteData()) {
                if (selectedControls.length)
                    selectedControls.push(' ');
                selectedControls.push(
                    r.button(i18n.t('button.delete_selected'), '#delete', {
                        props: {
                            onclick: (e) => {
                                e.preventDefault();
                                this.deleteSelectedItems();
                            },
                        },
                    })
                );
            }

            if (selectedControls.length) {
                cells.push(
                    r.cell({ css: '.shrink.ml-1' }, selectedControls)
                );
            }
        }

        return r.row({ css: '.align-center' }, cells);

    };

    _render(r, vm) {

        const extraControls = this.getControls()
            .filter((control) => control !== this.uiDataTable)
            .map((control) => control.render(r, vm));

        return r.module({}, [
            r.header({}, [
                this.renderHeaderContent(r, vm, false),
            ]),
            r.section({}, [
                this.uiDataTable.render(r, vm),
            ]),
            r.footer({}, [
                r.nav({}, this.renderFooterContent(r, vm, true)),
            ]),
        ].concat(extraControls));

    };

}
